import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Icons from "../icons";
import { useTheme } from "../theme";
import TicketLinkField from "./TicketLinkField";
import { DIALOG_HEIGHT } from "./dialogSize";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

const VALID_ACTIONS = new Set([
  "fill",
  "type",
  "click",
  "press",
  "selectOption",
  "selectDropdown",
  // Whole-interaction primitives — no selector, no value. Must stay in step
  // with AuthoredStep in app/agent/graph.py: anything missing from this set is
  // silently DROPPED on save, which is how authored esignSingle steps were
  // disappearing between the generation preview and the stored test case.
  "esign",
  "esignSingle",
  "esignDouble",
  "closeDialog",
  "expectVisible",
  "expectHidden",
  "expectText",
  "expectUrlContains",
  "expectDisabled",
  "expectEnabled",
  "expectFocused",
  "expectNotFocused",
  "expectValue",
  // A date set through its calendar; the date is worked out at run time.
  "pickDate",
  // Pointing and values (app/hybrid/extra_steps.py).
  "hover",
  "keepValue",
  "expectLength",
  "expectDistinct",
  "expectCharsIn",
  // How many elements match, and whether a field can be changed.
  "expectCount",
  "expectReadOnly",
  "expectEditable",
  // A pause, in seconds (value).
  "wait",
  // A second browser session in the run (app/hybrid/run_sessions.py).
  "openSession",
  "useSession",
  "setRolePermission",
]);

/* Older gpt-4o-mini responses occasionally leak the raw tool-call envelope
   ({recipient_name, parameters}) into the `steps` array instead of proper
   step objects. Drop those; keep only well-formed steps. */
function normalizeStep(s) {
  if (!s || typeof s !== "object") return null;
  if (!s.action || !VALID_ACTIONS.has(s.action)) return null;
  const out = {
    action: s.action,
    selector: s.selector ?? null,
    value: s.value ?? null,
  };
  // Preserve the Xray step back-reference so imported cases keep their mapping.
  if (s.source_step_no != null) out.source_step_no = s.source_step_no;
  return out;
}

/* Menu flattening used to live here, duplicating the server's walk of the
   PlantOps tree. The page list now comes pre-flattened from
   GET /api/plantops/pages as [{id, label, urlpath, path}], where id is the
   per-tenant menuitemkey the runner stores and urlpath is the stable identifier
   matching componentDictionary.tsx keys. */

/* Match a Jira Module/Page value against the PlantOps menu list.
   Tries exact match first (case-insensitive), then falls back to
   substring/contains matching so "Organization" matches a menu item
   labelled "Organization Page" or vice versa. */
function matchMenuByLabel(label, menuOptions) {
  if (!label || !Array.isArray(menuOptions)) return null;
  const target = String(label).trim().toLowerCase();
  if (!target) return null;
  // 1. Exact match
  const exact = menuOptions.find(
    (o) =>
      String(o.label || "")
        .trim()
        .toLowerCase() === target,
  );
  if (exact) return exact;
  // 2. Menu label contains the Jira value (e.g. menu="Organization Page", jira="Organization")
  const contains = menuOptions.find((o) =>
    String(o.label || "")
      .trim()
      .toLowerCase()
      .includes(target),
  );
  if (contains) return contains;
  // 3. Jira value contains the menu label (e.g. jira="Organization Page", menu="Organization")
  const reverse = menuOptions.find((o) => {
    const ml = String(o.label || "")
      .trim()
      .toLowerCase();
    return ml.length >= 3 && target.includes(ml);
  });
  if (reverse) return reverse;
  return null;
}

/* Score PlantOps menu options against a Jira ticket's Components/labels/summary
   and return the best match, or null if nothing scores well. Mirrors the
   Python `suggest_menu_match` in app/services/jira_service.py so both places
   stay consistent — runs client-side to avoid an extra round-trip.
   Used only as a fallback when the ticket has no Module/Page value. */
const JIRA_STOPWORDS = new Set([
  "a",
  "an",
  "and",
  "as",
  "at",
  "be",
  "by",
  "for",
  "from",
  "in",
  "is",
  "it",
  "of",
  "on",
  "or",
  "that",
  "the",
  "this",
  "to",
  "we",
  "with",
  "add",
  "new",
  "update",
  "fix",
  "bug",
  "issue",
  "feature",
  "user",
  "users",
  "page",
  "screen",
]);
function suggestMenuMatch(ticket, menuOptions) {
  if (!ticket || !Array.isArray(menuOptions) || menuOptions.length === 0)
    return null;
  const components = (ticket.components || [])
    .map((c) => (c || "").trim())
    .filter(Boolean);
  const labels = (ticket.labels || [])
    .map((l) => (l || "").trim())
    .filter(Boolean);
  const summaryWords = new Set(
    ((ticket.summary || "").toLowerCase().match(/[a-z0-9]+/g) || []).filter(
      (w) => w.length >= 3 && !JIRA_STOPWORDS.has(w),
    ),
  );
  const screenHints = labels
    .filter((l) => l.toLowerCase().startsWith("screen:") && l.includes(":"))
    .map((l) => l.split(":", 2)[1].trim().toLowerCase());

  let best = null;
  for (const opt of menuOptions) {
    const label = String(opt.label || "").trim();
    if (!label) continue;
    const labelLc = label.toLowerCase();
    let score = 0;
    const reasons = [];

    for (const comp of components) {
      const cLc = comp.toLowerCase();
      if (cLc === labelLc) {
        score += 3;
        reasons.push(`matched Component '${comp}'`);
      } else if (cLc && (labelLc.includes(cLc) || cLc.includes(labelLc))) {
        score += 2;
        reasons.push(`partial Component match '${comp}'`);
      }
    }
    for (const hint of screenHints) {
      if (hint === labelLc || (hint && labelLc.includes(hint))) {
        score += 2;
        reasons.push(`matched label 'screen:${hint}'`);
      }
    }
    const labelWords = new Set(labelLc.match(/[a-z0-9]+/g) || []);
    const overlap = [...summaryWords].filter((w) => labelWords.has(w));
    if (overlap.length) {
      score += Math.min(overlap.length, 3);
      reasons.push(`summary words: ${overlap.slice(0, 3).sort().join(", ")}`);
    }

    if (score >= 3 && (!best || score > best.confidence)) {
      best = {
        menuId: opt.id,
        confidence: score,
        reason: reasons[0] || "keyword match",
      };
    }
  }
  return best;
}

function apiWsUrl(sessionId) {
  const base = new URL(API_BASE_URL, window.location.origin);
  const proto = base.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${base.host}/api/ws/generate/${sessionId}`;
}

const inputCls =
  "w-full rounded-lg border border-slate-300 bg-card px-3 py-2 text-sm text-slate-900 placeholder-slate-400 outline-none transition focus:border-brand-600 focus:ring-2 focus:ring-brand-600/25";

/* ------------------------------------------------------------------ */
/*  Custom dropdown                                                    */
/* ------------------------------------------------------------------ */
function Dropdown({
  value,
  options,
  onChange,
  placeholder = "— select —",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);
  const searchRef = useRef(null);
  const selected = options.find((o) => o.id === value);
  const isEmpty = options.length === 0;

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) =>
      ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  // Reset the filter each time the menu opens and focus the search box so the
  // user can type immediately.
  useEffect(() => {
    if (open) {
      setQuery("");
      const t = setTimeout(() => searchRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open]);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? options.filter((o) =>
        String(o.label || "")
          .toLowerCase()
          .includes(q),
      )
    : options;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => !disabled && !isEmpty && setOpen((o) => !o)}
        disabled={disabled || isEmpty}
        className="flex w-full items-center justify-between gap-2 rounded-lg border border-slate-300 bg-card px-3 py-2 text-sm text-slate-900 transition hover:border-slate-400 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400"
      >
        <span className={selected ? "" : "text-slate-400"}>
          {selected ? selected.label : placeholder}
        </span>
        <Icons.ChevronDown
          className={[
            "h-4 w-4 text-slate-400 transition-transform",
            open ? "rotate-180" : "",
          ].join(" ")}
        />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-slate-200 bg-card shadow-lg">
          {/* Search box — filters the list so long option sets stay compact. */}
          <div className="border-b border-slate-100 p-1.5">
            <div className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2">
              <Icons.Search className="h-3.5 w-3.5 flex-none text-slate-400" />
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search…"
                className="w-full bg-transparent py-1.5 text-sm text-slate-900 placeholder-slate-400 outline-none"
              />
            </div>
          </div>
          <ul className="max-h-52 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-sm text-slate-400">No matches</li>
            ) : (
              filtered.map((opt) => (
                <li key={opt.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange(opt.id);
                      setOpen(false);
                    }}
                    className={[
                      "flex w-full items-center justify-between px-3 py-2 text-sm transition",
                      opt.id === value
                        ? "bg-brand-50 font-medium text-brand-700"
                        : "text-slate-700 hover:bg-slate-50",
                    ].join(" ")}
                  >
                    {opt.label}
                    {opt.id === value && <Icons.Check className="h-4 w-4" />}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Create test case page                                              */
/* ------------------------------------------------------------------ */
export default function CreateTestCasePage({
  board,
  subcategorykey = null,
  onCancel,
  onCreate,
  presetTickets = [],
  lockTickets = false,
  /* Which Jira issue to discover Xray tests FROM. Normally the linked story,
     but tests can be linked to the epic instead, in which case the board sends
     the epic key here while the created scripts still link to a story. */
  discoverKey = "",
  /* Xray Test keys to arrive pre-ticked, when the caller already chose them. */
  initialXrayKeys = [],
  /* The chosen tests are attached to no story and no epic, so there is no
     parent to discover them through — fetch them by key instead. */
  xrayUnlinked = false,
  // Kept for callers that still pass it. The wording below no longer varies by
  // entry point — every flow here authors Playwright scripts.
  variant = "testcase", // eslint-disable-line no-unused-vars
}) {
  const { userkey } = useTheme();
  // This page always authors a group of Playwright steps, whichever entry point
  // you came from (board, ticket or Explore & Author), so the wording is the
  // same everywhere.
  const pageTitle = "Create Artifcats";
  const [phase, setPhase] = useState("form"); // "form" | "generating"
  // The script-group name is derived from the Jira story (the page no longer
  // asks for it) — see the auto-name effect below.
  const [name, setName] = useState("Artifacts");
  const [priority] = useState("Medium");
  const [connectTo, setConnectTo] = useState("");
  const [connections, setConnections] = useState([]);
  const [connectionsLoading, setConnectionsLoading] = useState(true);
  const [connectionsError, setConnectionsError] = useState("");
  // When arriving from the Tickets view with a linked Jira ticket, default to
  // the Jira source tab and pre-fill the key so the user doesn't have to type it.
  const presetJiraKey = presetTickets?.[0]?.ticket_key || "";
  const [sourceMode, setSourceMode] = useState(
    presetJiraKey ? "jira" : "manual",
  ); // "github" | "jira" | "manual"
  const [jiraTicket, setJiraTicket] = useState(presetJiraKey);
  const [description, setDescription] = useState("");
  const [aiContext, setAiContext] = useState("");
  // Optional Jira tickets (Testing status) to link every generated case to.
  const [linkTickets, setLinkTickets] = useState(presetTickets);
  // Enriched changelog entries (UI repo, last N commits with diffs + modules).
  // Shape: { repo, branch, component_map_size,
  //          entries: [{commit, short_sha, date, author, body, files,
  //                     affected_modules: [{menu_key, files_count, sample_files}],
  //                     other_files: [], total_files, error?}] }
  const [entriesData, setEntriesData] = useState(null);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [entriesError, setEntriesError] = useState("");
  // "latest" pulls the newest N commits from CHANGELOG.md; "commit" fetches
  // one specific SHA (any branch) via /api/github/commit/{sha}. Both feed
  // the same entries-list renderer below.
  const [githubMode, setGithubMode] = useState("latest"); // "latest" | "commit"
  const [entriesLimit, setEntriesLimit] = useState(10); // 1..25 (backend cap)
  const [commitInput, setCommitInput] = useState("");
  // Which changelog entry the user picked, and which modules from it.
  const [selectedCommit, setSelectedCommit] = useState("");
  const [checkedMenuKeys, setCheckedMenuKeys] = useState([]);
  // Jira ticket snapshot. Shape: {key, url, summary, description_text, status, priority, issue_type, components, labels}
  const [jiraData, setJiraData] = useState(null);
  const [jiraLoading, setJiraLoading] = useState(false);
  const [jiraError, setJiraError] = useState("");
  const [jiraAutoPicked, setJiraAutoPicked] = useState(null); // {menuId, reason}
  // Xray discovery (button-triggered). Shape of xrayData:
  // {story_key, story_summary, test_keys, test_set_keys, tests:[{issue_id,key,
  //  summary,test_type,steps:[{step_no,action,data,result}]}], count}
  const [xrayData, setXrayData] = useState(null);
  const [xrayLoading, setXrayLoading] = useState(false);
  const [xrayError, setXrayError] = useState("");
  const [xrayChecked, setXrayChecked] = useState(false); // has discovery run yet?
  const [xraySelected, setXraySelected] = useState(() => new Set()); // issue_ids to import
  const [xrayExpanded, setXrayExpanded] = useState(() => new Set()); // issue_ids showing steps
  // Duplicate guard. After discovery we ask /api/coverage/xray which of the
  // discovered Xray Tests ALREADY have a Cortex/Playwright test case. Shape:
  // {items: {XRAY-KEY: {automated, drifted, testcase_count, testcases:[…]}},
  //  automated_keys, drifted_keys, new_keys, total}
  const [xrayCoverage, setXrayCoverage] = useState(null);
  const [xrayCoverageLoading, setXrayCoverageLoading] = useState(false);
  // Off by default: already-automated tests stay un-ticked so an existing
  // Playwright test case is never replaced by accident (an Xray test keeps one).
  const [xrayAllowDuplicates, setXrayAllowDuplicates] = useState(false);
  // When on (and Xray tests are found + selected), Generate runs in "xray" mode:
  // the AI authors Playwright steps against each pre-written Xray step instead
  // of inventing cases from the description.
  const [importXray, setImportXray] = useState(true);
  // Retained for payload/preview compatibility only. The background context
  // (epic / story / test set / test plan / preconditions) is now ALWAYS shared
  // with the AI — the user no longer opts in — so this stays empty.
  const [xrayContext, setXrayContext] = useState(() => new Set());
  // "View final context" preview modal.
  const [xrayCtxOpen, setXrayCtxOpen] = useState(false);
  const [xrayCtxText, setXrayCtxText] = useState("");
  const [xrayCtxLoading, setXrayCtxLoading] = useState(false);
  const [xrayCtxError, setXrayCtxError] = useState("");
  const [provider, setProvider] = useState("gemini"); // "openai" | "gemini"
  // Model choices are fetched live from the backend (/api/models?provider=…)
  // whenever the provider changes — never hardcoded. `model` is the exact id
  // the backend passes to the LLM client; "" means "use the env default".
  const [model, setModel] = useState("");
  const [modelOptions, setModelOptions] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsError, setModelsError] = useState("");
  const [autoLogin, setAutoLogin] = useState(true);
  // Login-page mode is chosen via the "Connect to" dropdown's special
  // "Login Page" option (id "__login__"). When selected, the backend skips
  // auto-login + navigation and authors tests on the sign-in screen. Deriving
  // it from connectTo keeps the whole flow driven by that one dropdown.
  const targetLogin = connectTo === "__login__";
  // Tracks whether generation was started (new tab opened) — shows a banner
  // and disables the Generate button until the tab signals back.
  const [generationStarted, setGenerationStarted] = useState(false);

  // Fetch the selectable models for the current provider. Re-runs on provider
  // switch; pre-selects the provider's configured default (falls back to the
  // first model returned).
  useEffect(() => {
    let cancelled = false;
    setModelsLoading(true);
    setModelsError("");
    setModelOptions([]);
    setModel("");
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}models?provider=${encodeURIComponent(provider)}`,
        );
        const body = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok) {
          setModelsError(
            body?.detail || `Could not load models (HTTP ${res.status})`,
          );
          return;
        }
        const opts = Array.isArray(body?.data?.models) ? body.data.models : [];
        setModelOptions(opts);
        const def = body?.data?.default;
        const defMatch = opts.find((o) => o.id === def);
        setModel(defMatch ? defMatch.id : opts[0]?.id || "");
      } catch (e) {
        if (!cancelled)
          setModelsError(`Network error loading models: ${e.message}`);
      } finally {
        if (!cancelled) setModelsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [provider]);

  // The app pages this test case can be connected to.
  //
  // This used to pull the PlantOps api key out of /plantops/config and call
  // PlantOps' global /menus straight from the browser. That broke when PlantOps
  // made the menu per-user (/security/user/{userkey}/menu), and it also put the
  // api key in the page where anything could read it. Our own endpoint already
  // resolves the right user, flattens the tree and keeps the key server-side.
  const loadConnections = useCallback(async () => {
    setConnectionsLoading(true);
    setConnectionsError("");
    try {
      const res = await fetch(`${API_BASE_URL}plantops/pages`);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `HTTP ${res.status}`);
      const list = Array.isArray(body?.data) ? body.data : [];
      setConnections(list);
      if (list.length === 0) {
        setConnectionsError("The PlantOps menu service returned no pages.");
      }
    } catch (e) {
      setConnections([]);
      setConnectionsError(e.message || "Could not load the app page list.");
    } finally {
      setConnectionsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  // GitHub changelog fetch (used when sourceMode === "github" +
  // githubMode === "latest"). Pulls the last N commit entries with their
  // diffs and affected modules already joined against componentDictionary.tsx.
  const loadEntries = useCallback(async (limitArg) => {
    const n = Math.max(1, Math.min(25, Number(limitArg) || 10));
    setEntriesLoading(true);
    setEntriesError("");
    try {
      const res = await fetch(
        `${API_BASE_URL}github/changelog/entries?limit=${n}`,
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setEntriesData(null);
        setEntriesError(body?.detail || `HTTP ${res.status}`);
        return;
      }
      const data = body?.data || null;
      setEntriesData(data);
      // Auto-select the newest entry so the affected-modules chips are
      // visible without an extra click. Modules stay unchecked — the user
      // has to opt into what they want to test.
      const first = data?.entries?.[0];
      if (first?.commit) {
        setSelectedCommit(first.commit);
        setCheckedMenuKeys([]);
      }
    } catch (e) {
      setEntriesData(null);
      setEntriesError(`Network error: ${e.message}`);
    } finally {
      setEntriesLoading(false);
    }
  }, []);

  // Fetch one commit by SHA (any branch reachable in the repo). The response
  // is fit into the same {entries: [...]} shape the list renderer already
  // handles, so the rest of the flow (module ticks, generate) is unchanged.
  const loadSingleCommit = useCallback(async (shaArg) => {
    const trimmed = (shaArg || "").trim();
    if (!/^[0-9a-f]{6,40}$/i.test(trimmed)) {
      setEntriesError("Invalid commit SHA — enter 6–40 hex characters.");
      return;
    }
    setEntriesLoading(true);
    setEntriesError("");
    try {
      const res = await fetch(
        `${API_BASE_URL}github/commit/${encodeURIComponent(trimmed)}`,
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setEntriesData(null);
        setEntriesError(body?.detail || `HTTP ${res.status}`);
        return;
      }
      const detail = body?.data || null;
      if (!detail?.entry) {
        setEntriesData(null);
        setEntriesError("No commit data returned.");
        return;
      }
      const data = {
        repo: detail.repo,
        branch: detail.branch,
        component_map_size: detail.component_map_size,
        entries: [detail.entry],
      };
      setEntriesData(data);
      setSelectedCommit(detail.entry.commit);
      setCheckedMenuKeys([]);
    } catch (e) {
      setEntriesData(null);
      setEntriesError(`Network error: ${e.message}`);
    } finally {
      setEntriesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (sourceMode !== "github") return;
    if (githubMode !== "latest") return; // don't auto-fetch in commit mode
    if (entriesData || entriesLoading) return;
    loadEntries(entriesLimit);
    // Intentionally not re-firing on entriesLimit changes — user drives that
    // with the Refresh button so typing "1", "12" doesn't cascade fetches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceMode, githubMode, entriesData, entriesLoading, loadEntries]);

  // When the picked entry changes, clear any module ticks that don't exist
  // in the new entry (avoids stale keys leaking across commits).
  useEffect(() => {
    if (!selectedCommit || !entriesData) return;
    const entry = entriesData.entries?.find((e) => e.commit === selectedCommit);
    const validKeys = new Set(
      (entry?.affected_modules || []).map((m) => m.menu_key),
    );
    setCheckedMenuKeys((prev) => prev.filter((k) => validKeys.has(k)));
  }, [selectedCommit, entriesData]);

  // The Jira auto-pick hint is only meaningful on the Jira tab; drop it if the
  // user switches tabs so the "Auto-picked from ticket" line doesn't leak.
  useEffect(() => {
    if (sourceMode !== "jira") setJiraAutoPicked(null);
  }, [sourceMode]);

  // Jira ticket fetch (used when sourceMode === "jira"). Called on button click.
  const loadJiraTicket = useCallback(async (rawInput, menuOptions) => {
    const q = (rawInput || "").trim();
    if (!q) {
      setJiraError("Enter a ticket key (e.g. PROJ-123) or a Jira URL.");
      return;
    }
    setJiraLoading(true);
    setJiraError("");
    setJiraAutoPicked(null);
    try {
      const res = await fetch(
        `${API_BASE_URL}jira/ticket?key=${encodeURIComponent(q)}`,
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setJiraData(null);
        setJiraError(body?.detail || `HTTP ${res.status}`);
        return;
      }
      const data = body?.data || null;
      setJiraData(data);
      if (data) {
        // Prefer the Jira Module/Page custom field — authoritative when set
        // and matches an existing menu leaf. Fall back to keyword scoring.
        let picked = null;
        if (data.module_page) {
          const strong = matchMenuByLabel(data.module_page, menuOptions || []);
          if (strong) {
            picked = {
              menuId: strong.id,
              reason: `Module/Page: ${data.module_page}`,
              source: "module_page",
            };
          }
        }
        if (!picked) {
          const scored = suggestMenuMatch(data, menuOptions || []);
          if (scored) picked = { ...scored, source: "keyword" };
        }
        if (picked) {
          setConnectTo(picked.menuId);
          setJiraAutoPicked(picked);
        } else {
          setConnectTo("");
        }
      }
    } catch (e) {
      setJiraData(null);
      setJiraError(`Network error: ${e.message}`);
    } finally {
      setJiraLoading(false);
    }
  }, []);

  // Xray discovery — button-triggered. Reaches out to Xray for the Tests /
  // Test Sets linked to the currently-loaded Jira story and shows the user
  // exactly which pre-authored tests (with their Action/Data/Expected steps)
  // will be imported. Defaults every discovered test to selected.
  /* A stable signature of the caller's pre-selection, so loadXrayTests can read
     it without being rebuilt on every render by a fresh array identity. */
  const wantedKeysSig = (initialXrayKeys || []).join(",");
  const loadXrayTests = useCallback(async (storyKey) => {
    const q = (storyKey || "").trim();
    /* `byKeys` covers Xray tests attached to no story and no epic: there is no
       parent to discover them through, so they are fetched by key instead. */
    const byKeys = storyKey === null;
    if (!q && !byKeys) return;
    setXrayLoading(true);
    setXrayError("");
    setXrayChecked(true);
    setXrayCoverage(null);
    try {
      const res = await fetch(
        byKeys
          ? `${API_BASE_URL}xray/discover?tests=${encodeURIComponent(wantedKeysSig)}`
          : `${API_BASE_URL}xray/discover?story=${encodeURIComponent(q)}`,
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setXrayData(null);
        setXrayError(body?.detail || `HTTP ${res.status}`);
        return;
      }
      const data = body?.data || null;
      setXrayData(data);
      setXrayExpanded(new Set());

      // Which of these already have a Playwright test case? Ask before
      // pre-selecting, so we never tick something that would be a duplicate.
      const found = data?.tests || [];
      let automated = new Set();
      if (found.length > 0) {
        setXrayCoverageLoading(true);
        try {
          const cRes = await fetch(`${API_BASE_URL}coverage/xray`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              tests: found.map((t) => ({
                key: t.key,
                summary: t.summary || "",
                steps: t.steps || [],
              })),
            }),
          });
          const cBody = await cRes.json().catch(() => ({}));
          if (cRes.ok && cBody?.data) {
            setXrayCoverage(cBody.data);
            automated = new Set(cBody.data.automated_keys || []);
          }
        } catch {
          setXrayCoverage(null); // fail open — user still sees the tests
        } finally {
          setXrayCoverageLoading(false);
        }
      }
      /* An explicit selection from the caller wins — they already picked which
         tests to automate, so do not second-guess it. Otherwise pre-select
         only what isn't already automated. */
      const wanted = new Set(wantedKeysSig ? wantedKeysSig.split(",") : []);
      setXraySelected(
        new Set(
          (wanted.size > 0
            ? found.filter((t) => wanted.has(t.key))
            : found.filter((t) => !automated.has(t.key))
          ).map((t) => t.issue_id),
        ),
      );
      setXrayAllowDuplicates(
        wanted.size > 0 && found.some((t) => wanted.has(t.key) && automated.has(t.key)),
      );
    } catch (e) {
      setXrayData(null);
      setXrayError(`Network error: ${e.message}`);
    } finally {
      setXrayLoading(false);
    }
  }, [wantedKeysSig]);

  // Reset Xray discovery whenever the loaded Jira story changes/clears so the
  // panel never shows tests from a previously-loaded ticket. Playwright scripts
  // are always built from a story's Xray tests, so as soon as a story is loaded
  // we (a) name the script group after it and (b) auto-run Xray discovery
  // instead of waiting for a manual "Check Xray" click.
  useEffect(() => {
    setXrayData(null);
    setXrayError("");
    setXrayChecked(false);
    setXraySelected(new Set());
    setXrayExpanded(new Set());
    setXrayContext(new Set());
    setXrayCoverage(null);
    setXrayAllowDuplicates(false);
    if (jiraData?.key) {
      setName(
        `${jiraData.key}${jiraData.summary ? ` — ${jiraData.summary}` : ""}`.slice(0, 200)
      );
      loadXrayTests(xrayUnlinked ? null : discoverKey || jiraData.key);
    } else {
      setName("Artifacts");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jiraData?.key, discoverKey, xrayUnlinked]);

  // Guard the checkbox itself: ticking an already-automated test requires the
  // explicit "author anyway" override, so a stray click can't create a duplicate.
  const automatedKeySet = useMemo(
    () => new Set(xrayCoverage?.automated_keys || []),
    [xrayCoverage],
  );
  const isAlreadyAutomated = useCallback(
    (test) => automatedKeySet.has(test?.key),
    [automatedKeySet],
  );

  // Turning the override off un-ticks anything already automated, so the
  // selection always matches the promise made in the banner.
  useEffect(() => {
    if (xrayAllowDuplicates || automatedKeySet.size === 0) return;
    const automatedIds = new Set(
      (xrayData?.tests || [])
        .filter((t) => automatedKeySet.has(t.key))
        .map((t) => t.issue_id),
    );
    setXraySelected((prev) => {
      if (!Array.from(prev).some((id) => automatedIds.has(id))) return prev;
      return new Set(Array.from(prev).filter((id) => !automatedIds.has(id)));
    });
  }, [xrayAllowDuplicates, automatedKeySet, xrayData]);

  const toggleXraySelected = useCallback((issueId) => {
    setXraySelected((prev) => {
      const next = new Set(prev);
      if (next.has(issueId)) next.delete(issueId);
      else next.add(issueId);
      return next;
    });
  }, []);

  const toggleXrayExpanded = useCallback((issueId) => {
    setXrayExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(issueId)) next.delete(issueId);
      else next.add(issueId);
      return next;
    });
  }, []);

  // Preview the exact extra-context block that will be sent to the AI for the
  // currently-selected tests + context options.
  const previewXrayContext = async () => {
    setXrayCtxOpen(true);
    setXrayCtxText("");
    setXrayCtxError("");
    setXrayCtxLoading(true);
    try {
      const keys = (xrayData?.tests || [])
        .filter((t) => xraySelected.has(t.issue_id))
        .map((t) => t.key);
      const res = await fetch(`${API_BASE_URL}xray/context`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          story_key: jiraData?.key || "",
          test_keys: keys,
          options: Array.from(xrayContext),
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setXrayCtxError(body?.detail || `HTTP ${res.status}`);
        return;
      }
      setXrayCtxText((body?.data?.context || "").trim());
    } catch (e) {
      setXrayCtxError(`Failed to load: ${e.message}`);
    } finally {
      setXrayCtxLoading(false);
    }
  };

  // pre-filled key. Waits until the connections list has loaded so that the
  // auto-pick "Connect to" logic inside loadJiraTicket works correctly.
  const autoLoadedRef = useRef(false);
  useEffect(() => {
    if (autoLoadedRef.current) return;
    if (!presetJiraKey || connectionsLoading) return;
    autoLoadedRef.current = true;
    loadJiraTicket(presetJiraKey, connections);
  }, [presetJiraKey, connectionsLoading, connections, loadJiraTicket]);

  // Generation phase state.
  const [consoleLines, setConsoleLines] = useState([]);
  const [steps, setSteps] = useState([]);
  const [authoredCases, setAuthoredCases] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [done, setDone] = useState(false);
  const [frameUrl, setFrameUrl] = useState(null);
  // Multi-module queue: one entry per module the user checked. For non-GitHub
  // sources the queue has exactly one item derived from `connectTo`.
  const [moduleQueue, setModuleQueue] = useState([]);
  const [queueIndex, setQueueIndex] = useState(0);
  const wsRef = useRef(null);
  const consoleEndRef = useRef(null);
  // Ref to the currently-running queue module. `handleAgentEvent` reads it to
  // tag each authored test case with the module that produced it, so `save`
  // can persist per-case menu info instead of a single global connectTo.
  const currentModuleRef = useRef(null);

  // Currently-picked changelog entry (memoized so we don't scan on every render).
  const selectedEntry =
    entriesData?.entries?.find((e) => e.commit === selectedCommit) || null;

  // URL paths of every menu leaf the backend can actually navigate to (built
  // from /menus). componentDictionary.tsx keys are matched against `urlpath`
  // (not menuitemkey — that's a per-tenant UUID). Used to reject "orphan"
  // component-dictionary entries whose key isn't a sidebar leaf, since the
  // backend requires a menu_path breadcrumb.
  const knownMenuKeys = new Set(connections.map((c) => c.urlpath));

  // Lookup helper: componentDictionary key (== urlpath) → flattened menu row.
  const findMenuByKey = (menuKey) =>
    connections.find((c) => c.urlpath === menuKey);

  // Only checked modules that we can actually resolve to a menu path count.
  const validCheckedMenuKeys = checkedMenuKeys.filter((k) =>
    knownMenuKeys.has(k),
  );

  // GitHub source needs: a picked commit + at least one valid module checked.
  const githubReady =
    Boolean(selectedCommit) && validCheckedMenuKeys.length > 0;
  // Jira source requires a successfully loaded ticket, not just any input text.
  const hasSource =
    sourceMode === "jira"
      ? Boolean(jiraData?.summary)
      : sourceMode === "manual"
        ? description.trim().length > 0
        : githubReady;
  // Non-GitHub still needs the "Connect to" dropdown filled. GitHub uses the
  // checked modules directly, so the dropdown isn't required.
  // Nothing left to import: the user asked for Xray import, tests were found,
  // but every one is already automated so none is ticked. Block Generate rather
  // than silently falling back to description-based generation — that fallback
  // would invent brand-new test cases the user never asked for.
  const xrayAllAlreadyDone =
    sourceMode === "jira" &&
    importXray &&
    (xrayData?.tests || []).length > 0 &&
    (xrayData?.tests || []).every((t) => !xraySelected.has(t.issue_id));

  const canGenerate = targetLogin
    ? true
    : hasSource &&
      !xrayAllAlreadyDone &&
      (sourceMode === "github"
        ? validCheckedMenuKeys.length > 0
        : Boolean(connectTo));

  // How many Xray tests are ticked for import (drives the footer summary).
  const selectedXrayCount = (xrayData?.tests || []).filter((t) =>
    xraySelected.has(t.issue_id)
  ).length;

  // What still needs doing before Generate lights up — shown in the footer so a
  // disabled button always explains itself.
  const readiness = targetLogin
    ? [{ ok: true, label: "Login-page test ready" }]
    : [
    {
      ok: hasSource,
      label:
        sourceMode === "jira"
          ? "Load a Jira story"
          : sourceMode === "manual"
            ? "Describe the requirement"
            : "Pick a GitHub commit",
    },
    ...(sourceMode === "github"
      ? [{ ok: validCheckedMenuKeys.length > 0, label: "Select at least one module" }]
      : [{ ok: Boolean(connectTo), label: "Choose a page to connect to" }]),
    ...(sourceMode === "jira" &&
    importXray &&
    (xrayData?.tests || []).length > 0
      ? [{ ok: !xrayAllAlreadyDone, label: "Select at least one test" }]
      : []),
  ];

  // Why Generate is disabled, for the button's tooltip.
  const blockingReasons = readiness.filter((r) => !r.ok).map((r) => r.label);
  const generateDisabledReason = generationStarted
    ? "Generation is already running — check the other tab or your Inbox."
    : blockingReasons.length
      ? `Can't generate yet — ${blockingReasons.join(", ")}.`
      : "";

  const connLabel =
    (connections.find((c) => c.id === connectTo) || {}).label || connectTo;
  const selectedModelLabel =
    (modelOptions.find((o) => o.id === model) || {}).label || model || "";
  const githubVersionSummary = selectedEntry
    ? `${selectedEntry.short_sha || selectedEntry.commit.slice(0, 7)} · ${selectedEntry.date || ""}`.trim()
    : "";
  const sourceSummary =
    sourceMode === "jira"
      ? jiraData
        ? `${jiraData.key}: ${jiraData.summary}`
        : "—"
      : sourceMode === "manual"
        ? description.trim() || "—"
        : githubVersionSummary || "—";
  const sourceSummaryLabel =
    sourceMode === "jira"
      ? "Ticket"
      : sourceMode === "github"
        ? "Commit"
        : "Description";

  /* --- Real generation: POST /api/features/generate, stream via WS.
         Called once per queued module — the useEffect re-runs when
         `queueIndex` advances after a session closes. */
  useEffect(() => {
    if (phase !== "generating") return undefined;
    const current = moduleQueue[queueIndex];
    if (!current) return undefined;
    currentModuleRef.current = current;

    let cancelled = false;
    let advanced = false;
    const advance = () => {
      if (advanced || cancelled) return;
      advanced = true;
      if (queueIndex + 1 < moduleQueue.length) {
        // Reset per-module transient state so the next module gets a fresh view.
        setFrameUrl(null);
        setQueueIndex((i) => i + 1);
      } else {
        setDone(true);
      }
    };

    const start = async () => {
      try {
        const prefix =
          moduleQueue.length > 1
            ? `[${queueIndex + 1}/${moduleQueue.length}] ${current.menu_label} — `
            : "";
        setConsoleLines((p) => [
          ...p,
          `> ${prefix}Connecting to ${current.menu_label}…`,
        ]);
        // Xray import mode: when the user loaded + selected linked Xray tests
        // (and left "import from Xray" on), author against those pre-written
        // tests instead of inventing from the description.
        const selectedXrayKeys = (xrayData?.tests || [])
          .filter((t) => xraySelected.has(t.issue_id))
          .map((t) => t.key);
        const useXray =
          sourceMode === "jira" && importXray && selectedXrayKeys.length > 0;
        const res = await fetch(`${API_BASE_URL}features/generate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: name.trim(),
            menu_label: current.menu_label,
            menu_path: current.menu_path,
            source_mode: useXray ? "xray" : sourceMode,
            ticket: sourceMode === "jira" ? jiraTicket.trim() : "",
            description: sourceMode === "manual" ? description.trim() : "",
            // Backend re-fetches the picked commit's diff as the source of
            // truth. `changelog_version` is just a human label for logs/UI.
            github_commit: sourceMode === "github" ? selectedCommit : "",
            changelog_version:
              sourceMode === "github" ? githubVersionSummary : "",
            // Xray import: the selected Test keys to author against + the story.
            xray_test_keys: useXray ? selectedXrayKeys : [],
            xray_story_key: useXray
              ? discoverKey || jiraData?.key || jiraTicket.trim()
              : "",
            // Extra context the user opted to share (epic/test set/plan/preconds).
            xray_context: useXray ? Array.from(xrayContext) : [],
            // Server-side duplicate backstop: without the explicit override the
            // API skips any Xray test that already has a Playwright case.
            xray_allow_duplicates: xrayAllowDuplicates,
            ai_context: aiContext.trim(),
            provider,
            model,
            auto_login: autoLogin,
            target_login: targetLogin,
          }),
        });
        const body = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok || !body?.data?.session_id) {
          setConsoleLines((p) => [
            ...p,
            `> ERROR: ${body?.detail || `Failed to start (HTTP ${res.status})`}`,
          ]);
          advance();
          return;
        }
        const sessionId = body.data.session_id;
        setConsoleLines((p) => [
          ...p,
          `> Session ${sessionId} started. Opening live stream…`,
        ]);

        const ws = new WebSocket(apiWsUrl(sessionId));
        wsRef.current = ws;
        ws.onmessage = (evt) => {
          let msg;
          try {
            msg = JSON.parse(evt.data);
          } catch {
            return;
          }
          handleAgentEvent(msg);
        };
        ws.onerror = () => {
          setConsoleLines((p) => [...p, "> WebSocket error."]);
        };
        ws.onclose = () => {
          advance();
        };
      } catch (e) {
        if (!cancelled) {
          setConsoleLines((p) => [...p, `> Network error: ${e.message}`]);
          advance();
        }
      }
    };

    start();

    return () => {
      cancelled = true;
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch {
          /* ignore */
        }
        wsRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, queueIndex, moduleQueue.length]);

  function handleAgentEvent(evt) {
    if (!evt || !evt.type) return;
    switch (evt.type) {
      case "info":
        if (evt.message) setConsoleLines((p) => [...p, `> ${evt.message}`]);
        break;
      case "act": {
        const argStr = evt.args
          ? Object.entries(evt.args)
              .map(([k, v]) => `${k}=${JSON.stringify(v)}`)
              .join(" ")
          : "";
        setConsoleLines((p) => [...p, `→ ${evt.tool} ${argStr}`.trimEnd()]);
        break;
      }
      case "frame":
        if (evt.data_url) setFrameUrl(evt.data_url);
        break;
      case "verify": {
        const mark = evt.status === "pass" ? "✓" : "✗";
        setConsoleLines((p) => [
          ...p,
          `${mark} [${evt.index}] ${evt.requirement} — ${(evt.status || "").toUpperCase()}${
            evt.evidence ? `  (${evt.evidence})` : ""
          }`,
        ]);
        break;
      }
      case "verify_summary":
        setConsoleLines((p) => [
          ...p,
          `> Verification: ${evt.passed}/${evt.total} passed, ${evt.failed} failed`,
        ]);
        break;
      case "testcase": {
        const stepsCount =
          evt.steps_count ?? (Array.isArray(evt.steps) ? evt.steps.length : 0);
        const fullSteps = Array.isArray(evt.steps) ? evt.steps : [];
        const mod = currentModuleRef.current;
        const moduleTag = mod?.menu_label ? `[${mod.menu_label}] ` : "";
        setSteps((p) => [
          ...p,
          `${moduleTag}[${evt.category}] ${evt.name} (${stepsCount} steps)`,
        ]);
        setAuthoredCases((p) => [
          ...p,
          {
            name: evt.name,
            category: evt.category,
            steps: fullSteps,
            // Xray provenance (present only for source_mode="xray"): the
            // original manual steps + metadata, used for the mapping display
            // and persisted into `source` on save.
            source_meta: evt.source_meta || null,
            xray_steps: Array.isArray(evt.xray_steps) ? evt.xray_steps : null,
            xray_test_key: evt.xray_test_key || "",
            // Snapshot the module that produced this case — save() uses these
            // instead of the (now global-per-run) connectTo.
            menu_key: mod?.menu_key || "",
            menu_label: mod?.menu_label || "",
            menu_path: mod?.menu_path || [],
          },
        ]);
        setConsoleLines((p) => [
          ...p,
          `✅ authored: ${moduleTag}[${evt.category}] ${evt.name} (${stepsCount} steps)`,
        ]);
        break;
      }
      case "authored":
        setConsoleLines((p) => [
          ...p,
          `> Authored ${evt.count} test case(s).${evt.summary ? " " + evt.summary : ""}`,
        ]);
        break;
      case "error":
        setConsoleLines((p) => [...p, `> ERROR: ${evt.message}`]);
        break;
      case "done":
        setConsoleLines((p) => [...p, "> Done."]);
        setDone(true);
        break;
      default:
        break;
    }
  }

  // Keep the console scrolled to the latest line.
  useEffect(() => {
    consoleEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [consoleLines]);

  const startGenerate = () => {
    if (!canGenerate) return;
    // Build the module queue: one entry per module the user targeted. For
    // non-GitHub sources this is a single item from `connectTo`; for GitHub
    // it's one entry per VALID checked module (menu_path resolvable from
    // /menus). Orphans are filtered — the backend requires menu_path and
    // we've already prevented them from being checked in the UI.
    let queue = [];
    if (targetLogin) {
      // Login-page mode: a single synthetic target with no sidebar path and no
      // real menu key. The backend stays on the login page and skips
      // auto-login/navigation.
      queue = [
        {
          menu_key: "",
          menu_label: "Login Page",
          menu_path: [],
        },
      ];
    } else if (sourceMode === "github") {
      queue = validCheckedMenuKeys
        .map((menuKey) => {
          const menu = findMenuByKey(menuKey);
          if (!menu?.path?.length) return null;
          return {
            menu_key: menu.id, // save uses the tenant UUID (id) as connectTo
            menu_label: menu.label || menuKey,
            menu_path: menu.path,
          };
        })
        .filter(Boolean);
      if (queue.length === 0) {
        setSaveError(
          "None of the checked modules are in the sidebar menu — can't auto-navigate.",
        );
        return;
      }
    } else {
      const menu = connections.find((c) => c.id === connectTo);
      queue = [
        {
          menu_key: connectTo,
          menu_label: menu?.label || connectTo,
          menu_path: menu?.path || [],
        },
      ];
    }
    setModuleQueue(queue);
    setQueueIndex(0);
    setConsoleLines([]);
    setSteps([]);
    setAuthoredCases([]);
    setSaveError("");
    setDone(false);
    setFrameUrl(null);

    // Determine Xray mode.
    const selectedXrayKeys = (xrayData?.tests || [])
      .filter((t) => xraySelected.has(t.issue_id))
      .map((t) => t.key);
    const useXray =
      sourceMode === "jira" && importXray && selectedXrayKeys.length > 0;

    // Store the full generation config for the new-tab GenerationView.
    const genConfig = {
      name: name.trim(),
      boardId: board.id,
      subcategorykey: subcategorykey || null,
      userkey: userkey || null,
      priority,
      sourceMode: useXray ? "xray" : sourceMode,
      jiraTicket: jiraTicket.trim(),
      jiraKey: jiraData?.key || "",
      jiraUrl: jiraData?.url || "",
      description: description.trim(),
      selectedCommit,
      githubVersionSummary,
      changelogRepo: entriesData?.repo || "",
      aiContext: aiContext.trim(),
      provider,
      model,
      autoLogin,
      targetLogin,
      connectTo,
      connLabel,
      connectToPath: connections.find((c) => c.id === connectTo)?.path || [],
      linkTicketKeys: linkTickets.map((t) => t.ticketkey),
      moduleQueue: queue,
      xrayTestKeys: useXray ? selectedXrayKeys : [],
      xrayStoryKey: useXray ? discoverKey || jiraData?.key || jiraTicket.trim() : "",
      xrayTests: useXray
        ? (xrayData?.tests || []).filter((t) => xraySelected.has(t.issue_id))
        : [],
      xrayAllowDuplicates,
    };
    localStorage.setItem("cortexqa_generation", JSON.stringify(genConfig));
    window.open(
      `${window.location.origin}${window.location.pathname}#/generate`,
      "_blank",
    );
    setGenerationStarted(true);
  };

  // Listen for completion from the generation tab via BroadcastChannel.
  useEffect(() => {
    let bc;
    try {
      bc = new BroadcastChannel("cortexqa_generation");
      bc.onmessage = (evt) => {
        const type = evt.data?.type;
        /* Progress / control chatter on the shared channel — not an end-of-run
           signal, so it must not unlock the Generate button mid-run. */
        if (type === "session-started" || type === "cancel-request") return;
        /* "sent-to-inbox" is the new flow — authored cases sit in the Inbox
           waiting for human approval before they land on the board. "saved"
           is kept so a stale tab from before the flow change still unblocks
           the UI instead of looking frozen. */
        if (type === "sent-to-inbox" || type === "saved") onCreate?.();
        // Reset the "generation started" state on any end signal from the tab.
        setGenerationStarted(false);
      };
    } catch {
      /* BroadcastChannel not supported */
    }
    return () => {
      try {
        bc?.close();
      } catch {
        /* ignore */
      }
    };
  }, [onCreate]);

  const save = async () => {
    if (saving) return;
    setSaveError("");
    if (authoredCases.length === 0) {
      setSaveError("Nothing to save — the AI didn't emit any test cases.");
      return;
    }
    setSaving(true);
    try {
      // Auto-group the whole generated batch under the name the user entered,
      // so the cases land as a single collapsible accordion instead of a flat
      // list of "<name> — <scenario>" rows. Group failure is non-fatal: fall
      // back to ungrouped so the cases still save.
      let groupkey = null;
      try {
        const grpRes = await fetch(
          `${API_BASE_URL}boards/${encodeURIComponent(board.id)}/groups`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              name: name.trim(),
              subcategorykey: subcategorykey || null,
              userkey: userkey || null,
              source: { mode: sourceMode, generated: true },
            }),
          },
        );
        const grpBody = await grpRes.json().catch(() => ({}));
        if (grpRes.ok) groupkey = grpBody?.data?.groupkey || null;
      } catch {
        groupkey = null;
      }
      let skipped = 0;
      let saved = 0;
      for (const c of authoredCases) {
        const cleanSteps = (c.steps || []).map(normalizeStep).filter(Boolean);
        if (cleanSteps.length === 0) {
          skipped += 1;
          continue;
        }
        // Each authored case carries the module that produced it (see the
        // "testcase" branch in handleAgentEvent). Fall back to connectTo so
        // legacy non-GitHub flows still save cleanly.
        const caseMenuKey = c.menu_key || connectTo;
        const caseMenuLabel = c.menu_label || connLabel;
        const caseMenuPath = c.menu_path?.length
          ? c.menu_path
          : connections.find((cn) => cn.id === connectTo)?.path || [];
        // When grouped, the group header already shows the batch name, so the
        // per-case name is just the scenario (module-tagged for multi-module
        // runs). Ungrouped fallback keeps the legacy "<name> — <scenario>".
        const caseName = groupkey
          ? moduleQueue.length > 1 && caseMenuLabel
            ? `[${caseMenuLabel}] ${c.name}`
            : c.name
          : moduleQueue.length > 1 && caseMenuLabel
            ? `${name.trim()} — [${caseMenuLabel}] ${c.name}`
            : `${name.trim()} — ${c.name}`;
        const res = await fetch(`${API_BASE_URL}testcases`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            board_id: board.id,
            subcategorykey: subcategorykey || null,
            groupkey,
            // name: `${name.trim()} — ${c.name}`,
            name: caseName,
            priority,
            userkey: userkey || null,
            menu_label: caseMenuLabel,
            menu_path: caseMenuPath,
            steps: cleanSteps,
            source: {
              creation: "ai",
              category: c.category,
              connectTo: caseMenuKey,
              mode: sourceMode,
              jiraTicket: jiraTicket.trim(),
              jiraKey: jiraData?.key || "",
              jiraUrl: jiraData?.url || "",
              description: description.trim(),
              changelogVersion: githubVersionSummary,
              changelogCommit: selectedCommit,
              changelogRepo: entriesData?.repo || "",
              // Xray provenance (type, xray_test_key, xray_test_id, steps_hash,
              // xray_steps) — present only for Xray-imported cases. Enables
              // step-level traceability, drift detection and Xray push-back.
              ...(c.source_meta || {}),
            },
            ai_context: aiContext.trim(),
            /* A sign-in test must not be auto-logged-in before it runs — its
               own steps are the login. Stored false so the record is honest;
               the runner also detects this from connectTo, which is what
               rescues cases saved before this. */
            auto_login: targetLogin ? false : autoLogin,
            link_ticket_keys: linkTickets.map((t) => t.ticketkey),
          }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body?.detail || `HTTP ${res.status}`);
        }
        saved += 1;
      }
      if (saved === 0) {
        setSaveError(
          "None of the authored cases had valid Artifact steps to save. Try regenerating.",
        );
        return;
      }
      if (skipped > 0) {
        setSaveError(
          `Saved ${saved}, skipped ${skipped} case(s) that had no executable steps.`,
        );
      }
      onCreate?.();
    } catch (e) {
      setSaveError(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const req = <span className="text-rose-500">*</span>;

  /* ================================================================= */
  /*  PHASE 2 — Generation view                                         */
  /* ================================================================= */
  if (phase === "generating") {
    return (
      <div className="flex h-[calc(100vh-8rem)] flex-col">
        {/* Top bar */}
        <div className="mb-4 flex-none">
          <button
            type="button"
            onClick={() => setPhase("form")}
            className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800"
          >
            <Icons.ArrowLeft className="h-4 w-4" />
            Back to details
          </button>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-xl font-semibold text-slate-900">
                Generating test case{moduleQueue.length > 1 ? "s" : ""}
              </h1>
              {moduleQueue.length > 1 && (
                <p className="mt-0.5 text-xs text-slate-500">
                  Module {Math.min(queueIndex + 1, moduleQueue.length)} of{" "}
                  {moduleQueue.length}
                  {moduleQueue[queueIndex]?.menu_label && !done
                    ? ` — ${moduleQueue[queueIndex].menu_label}`
                    : ""}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onCancel}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <div className="flex flex-col items-end gap-1">
                <button
                  type="button"
                  onClick={save}
                  disabled={!done || saving || authoredCases.length === 0}
                  className="inline-flex items-center gap-2 rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {saving || !done ? (
                    <Icons.Spinner className="h-4 w-4 animate-spin" />
                  ) : (
                    <Icons.Check className="h-4 w-4" />
                  )}
                  {saving
                    ? "Saving…"
                    : !done
                      ? "Generating…"
                      : `Save ${authoredCases.length} test case${authoredCases.length === 1 ? "" : "s"}`}
                </button>
                {saveError && (
                  <span className="text-xs font-medium text-rose-600">
                    {saveError}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200 bg-card shadow-sm">
          {/* Minimal summary strip */}
          <div className="flex flex-none flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3 text-xs">
            <span className="rounded-full bg-slate-100 px-2.5 py-1 font-medium text-slate-700">
              {name.trim()}
            </span>
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-600">
              {sourceSummaryLabel}:{" "}
              <span className="font-medium text-slate-800">
                {sourceSummary}
              </span>
            </span>
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-600">
              {moduleQueue.length > 1 ? "Modules" : "Connect to"}:{" "}
              <span className="font-medium text-slate-800">
                {moduleQueue.length > 1
                  ? moduleQueue.map((m) => m.menu_label).join(", ")
                  : moduleQueue[0]?.menu_label || connLabel}
              </span>
            </span>
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-600">
              Model:{" "}
              <span className="font-medium text-slate-800">
                {provider === "gemini" ? "Gemini" : "OpenAI"}
                {selectedModelLabel ? ` · ${selectedModelLabel}` : ""}
              </span>
            </span>
          </div>

          {/* Top: two columns (video | steps). Bottom: console */}
          <div className="flex min-h-0 flex-1 flex-col gap-3 p-4">
            {/* Top row */}
            <div className="flex min-h-0 flex-[3] gap-3">
              {/* Left — Playwright live view */}
              <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-lg border border-slate-800 bg-slate-900">
                {frameUrl ? (
                  <img
                    src={frameUrl}
                    alt="Live browser view"
                    className="max-h-full max-w-full object-contain"
                  />
                ) : (
                  <div className="flex flex-col items-center gap-2 text-slate-400">
                    <Icons.Play className="h-8 w-8" />
                    <p className="text-xs font-medium">
                      {done
                        ? "Recording ready"
                        : "Recording session…"}
                    </p>
                  </div>
                )}
                <span className="absolute left-3 top-3 rounded bg-black/40 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-300">
                  Live view
                </span>
              </div>

              {/* Right — Generated steps */}
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-slate-200">
                <div className="flex flex-none items-center gap-1.5 border-b border-slate-100 bg-slate-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  <Icons.Spark className="h-3.5 w-3.5 text-brand-600" />
                  Generated steps
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto p-3">
                  {steps.length === 0 ? (
                    <div className="flex h-full items-center justify-center gap-2 text-sm text-slate-400">
                      <Icons.Spinner className="h-4 w-4 animate-spin" />
                      Waiting for the AI to finish…
                    </div>
                  ) : (
                    <ol className="space-y-1.5">
                      {steps.map((s, i) => (
                        <li
                          key={i}
                          className="flex items-start gap-2 text-sm text-slate-700"
                        >
                          <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-brand-50 text-[11px] font-semibold text-brand-700">
                            {i + 1}
                          </span>
                          {s}
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              </div>
            </div>

            {/* Xray mapping — each Xray step and the Playwright steps authored
                against it (grouped by source_step_no). Shown only for imports. */}
            {authoredCases.some((c) => c.xray_steps?.length) && (
              <div className="max-h-64 flex-none overflow-y-auto rounded-lg border border-violet-200">
                <div className="flex flex-none items-center gap-1.5 border-b border-violet-100 bg-violet-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-violet-700">
                  <span className="rounded-full bg-violet-100 px-1.5 py-0.5 text-[9px] tracking-normal">
                    Test
                  </span>
                  Step mapping (Test step → Artifact)
                </div>
                <div className="space-y-3 p-3">
                  {authoredCases
                    .filter((c) => c.xray_steps?.length)
                    .map((c, ci) => {
                      const byStep = (c.steps || []).reduce((acc, st) => {
                        const k = st.source_step_no ?? 0;
                        (acc[k] = acc[k] || []).push(st);
                        return acc;
                      }, {});
                      return (
                        <div
                          key={ci}
                          className="rounded border border-slate-200 bg-white"
                        >
                          <div className="flex items-center gap-2 border-b border-slate-100 px-2.5 py-1.5 text-xs">
                            {c.xray_test_key && (
                              <span className="font-mono font-semibold text-violet-700">
                                {c.xray_test_key}
                              </span>
                            )}
                            <span className="truncate font-medium text-slate-800">
                              {c.name}
                            </span>
                          </div>
                          <div className="divide-y divide-slate-100">
                            {c.xray_steps.map((xs) => {
                              const mapped = byStep[xs.step_no] || [];
                              return (
                                <div key={xs.step_no} className="px-2.5 py-2">
                                  <div className="text-[11px] text-slate-700">
                                    <span className="mr-1 rounded bg-slate-100 px-1.5 py-0.5 font-semibold text-slate-500">
                                      Step {xs.step_no}
                                    </span>
                                    {xs.action}
                                    {xs.result ? (
                                      <span className="text-slate-400">
                                        {" "}
                                        — expected: {xs.result}
                                      </span>
                                    ) : null}
                                  </div>
                                  {mapped.length ? (
                                    <ul className="mt-1 space-y-0.5 pl-3">
                                      {mapped.map((st, si) => (
                                        <li
                                          key={si}
                                          className="flex items-start gap-1.5 text-[11px] text-slate-600"
                                        >
                                          <span className="rounded bg-brand-50 px-1.5 py-0.5 font-mono font-semibold text-brand-700">
                                            {st.action}
                                          </span>
                                          <span className="break-all">
                                            {st.selector}
                                            {st.value != null && st.value !== ""
                                              ? ` = "${st.value}"`
                                              : ""}
                                          </span>
                                        </li>
                                      ))}
                                    </ul>
                                  ) : (
                                    <div className="mt-1 pl-3 text-[11px] italic text-amber-600">
                                      No Artifact step found.
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                            {byStep[0]?.length ? (
                              <div className="px-2.5 py-2">
                                <div className="text-[11px] font-medium text-slate-400">
                                  Steps not tied to a test step
                                </div>
                                <ul className="mt-1 space-y-0.5 pl-3">
                                  {byStep[0].map((st, si) => (
                                    <li
                                      key={si}
                                      className="text-[11px] text-slate-500"
                                    >
                                      <span className="font-mono">
                                        {st.action}
                                      </span>{" "}
                                      {st.selector}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

            {/* Bottom — Console */}
            <div className="flex min-h-0 flex-[2] flex-col overflow-hidden rounded-lg border border-slate-800 bg-slate-950">
              <div className="flex-none border-b border-slate-800 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                Console
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2 font-mono text-xs leading-relaxed text-emerald-300">
                {consoleLines.length === 0 ? (
                  <span className="text-slate-500">Initializing…</span>
                ) : (
                  consoleLines.map((l, i) => <div key={i}>{l}</div>)
                )}
                <div ref={consoleEndRef} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ================================================================= */
  /*  PHASE 1 — Details form                                            */
  /* ================================================================= */
  return (
    <div className="flex h-[calc(100vh-8rem)] flex-col">
      {/* Top bar */}
      <div className="mb-4 flex-none">
        <button
          type="button"
          onClick={onCancel}
          className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800"
        >
          <Icons.ArrowLeft className="h-4 w-4" />
          {board.name}
        </button>

        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-gradient-to-br from-brand-600 to-violet-600 text-white shadow-lg shadow-brand-600/20">
              <Icons.Spark className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <h1 className="text-xl font-bold tracking-tight text-slate-900">{pageTitle}</h1>
              <p className="mt-0.5 truncate text-sm text-slate-500">
                {jiraData?.key ? (
                  <>
                    Automating <span className="font-mono font-medium text-slate-700">{jiraData.key}</span>
                    {jiraData.summary ? ` — ${jiraData.summary}` : ""}
                  </>
                ) : (
                  "AI-generate Artifacts from a story's tests."
                )}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="flex-none rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
        </div>
      </div>

      {/* Body: form (no sidebar) */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200 bg-card shadow-sm">
        <div className="min-h-0 flex-1 space-y-8 overflow-y-auto p-6">
          {/* Optional: link generated cases to a Jira Testing ticket */}
          <div className="max-w-xl">
            <TicketLinkField
              boardId={board.id}
              userkey={userkey}
              value={linkTickets}
              onChange={setLinkTickets}
              locked={lockTickets}
            />
          </div>

          {/* Source */}
          <section id="source" className="scroll-mt-4">
            <h2 className="text-sm font-semibold text-slate-900">
              Source {req}
            </h2>
            <p className="mb-3 mt-0.5 text-xs text-slate-500">
              {lockTickets
                ? "Artifacts are authored from this story's linked tests."
                : "Pull the latest GitHub changelog, a Jira ticket, or describe requirements manually."}
            </p>

            {/* Segmented toggle — hidden when the flow is locked to a ticket. */}
            {!lockTickets && (
            <div className="mb-4 inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
              {[
                { id: "github", label: "GitHub" },
                { id: "jira", label: "Jira" },
                { id: "manual", label: "Manual description" },
              ].map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => !opt.disabled && setSourceMode(opt.id)}
                  disabled={opt.disabled}
                  title={opt.disabled ? opt.badge : undefined}
                  className={[
                    "rounded-md px-3 py-1.5 text-sm font-medium transition",
                    opt.disabled
                      ? "cursor-not-allowed text-slate-400"
                      : sourceMode === opt.id
                        ? "bg-card text-brand-700 shadow-sm ring-1 ring-slate-200"
                        : "text-slate-500 hover:text-slate-800",
                  ].join(" ")}
                >
                  {opt.label}
                  {opt.badge && (
                    <span className="ml-1.5 rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                      {opt.badge}
                    </span>
                  )}
                </button>
              ))}
            </div>
            )}

            {sourceMode === "github" && (
              <div className="max-w-3xl">
                {/* Mode toggle: pull the newest N commits, or fetch one
                    specific SHA from any branch. Both feed the list below. */}
                <div className="mb-3 inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
                  {[
                    { id: "latest", label: "Latest N entries" },
                    { id: "commit", label: "Specific commit" },
                  ].map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => {
                        if (githubMode === opt.id) return;
                        setGithubMode(opt.id);
                        setEntriesData(null);
                        setEntriesError("");
                        setSelectedCommit("");
                        setCheckedMenuKeys([]);
                      }}
                      className={[
                        "rounded-md px-3 py-1.5 text-sm font-medium transition",
                        githubMode === opt.id
                          ? "bg-card text-brand-700 shadow-sm ring-1 ring-slate-200"
                          : "text-slate-500 hover:text-slate-800",
                      ].join(" ")}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                <div className="mb-2 flex flex-wrap items-end justify-between gap-3">
                  <label className="block text-sm font-medium text-slate-700">
                    {githubMode === "latest"
                      ? `Latest ${entriesLimit} changelog entr${entriesLimit === 1 ? "y" : "ies"}`
                      : "Load a specific commit"}
                    {entriesData?.repo && (
                      <span className="ml-1.5 font-normal text-slate-500">
                        · {entriesData.repo}
                      </span>
                    )}
                  </label>

                  {githubMode === "latest" ? (
                    <div className="flex items-center gap-2">
                      <label className="inline-flex items-center gap-1.5 text-xs text-slate-600">
                        Count
                        <input
                          type="number"
                          min={1}
                          max={25}
                          value={entriesLimit}
                          onChange={(e) => {
                            const raw = parseInt(e.target.value, 10);
                            if (Number.isNaN(raw)) return;
                            setEntriesLimit(Math.max(1, Math.min(25, raw)));
                          }}
                          className="w-16 rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-900 focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600/25"
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => {
                          setEntriesData(null);
                          setSelectedCommit("");
                          setCheckedMenuKeys([]);
                          loadEntries(entriesLimit);
                        }}
                        disabled={entriesLoading}
                        className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {entriesLoading ? (
                          <Icons.Spinner className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Icons.Spark className="h-3.5 w-3.5" />
                        )}
                        Refresh
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={commitInput}
                        onChange={(e) => setCommitInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            loadSingleCommit(commitInput);
                          }
                        }}
                        placeholder="Commit SHA (any branch, 6–40 hex)"
                        className="w-64 rounded-md border border-slate-300 px-2 py-1 font-mono text-xs text-slate-900 focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600/25"
                      />
                      <button
                        type="button"
                        onClick={() => loadSingleCommit(commitInput)}
                        disabled={entriesLoading || !commitInput.trim()}
                        className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {entriesLoading ? (
                          <Icons.Spinner className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Icons.Spark className="h-3.5 w-3.5" />
                        )}
                        Load commit
                      </button>
                    </div>
                  )}
                </div>

                {entriesLoading && (
                  <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-500">
                    <Icons.Spinner className="h-4 w-4 animate-spin" />
                    {githubMode === "latest"
                      ? `Loading CHANGELOG.md + last ${entriesLimit} commit diff${entriesLimit === 1 ? "" : "s"}…`
                      : "Fetching commit diff…"}
                  </div>
                )}

                {!entriesLoading && entriesError && (
                  <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
                    <div className="font-medium">Could not load changelog</div>
                    <div className="mt-1 text-xs">{entriesError}</div>
                  </div>
                )}

                {!entriesLoading && !entriesError && entriesData && (
                  <div className="space-y-2">
                    {(entriesData.entries || []).length === 0 && (
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-500">
                        No commit entries found in CHANGELOG.md.
                      </div>
                    )}
                    {(entriesData.entries || []).map((entry) => {
                      const isSelected = entry.commit === selectedCommit;
                      return (
                        <div
                          key={entry.commit}
                          className={[
                            "overflow-hidden rounded-lg border transition",
                            isSelected
                              ? "border-brand-400 bg-brand-50/40 ring-1 ring-brand-300"
                              : "border-slate-200 bg-slate-50 hover:border-slate-300",
                          ].join(" ")}
                        >
                          {/* Row header — click to select */}
                          <button
                            type="button"
                            onClick={() => setSelectedCommit(entry.commit)}
                            className="flex w-full items-start justify-between gap-3 px-3 py-2 text-left"
                          >
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 text-xs">
                                <span className="rounded-full bg-slate-200 px-2 py-0.5 font-mono text-[10px] font-semibold text-slate-700">
                                  {entry.short_sha || entry.commit.slice(0, 7)}
                                </span>
                                {entry.date && (
                                  <span className="text-slate-500">
                                    {entry.date}
                                  </span>
                                )}
                                {entry.author && (
                                  <span className="text-slate-500">
                                    · {entry.author}
                                  </span>
                                )}
                                <span className="ml-auto text-slate-400">
                                  {entry.total_files || 0} file
                                  {(entry.total_files || 0) === 1 ? "" : "s"}
                                </span>
                              </div>
                              {/* Affected module chips */}
                              <div className="mt-1.5 flex flex-wrap gap-1">
                                {(entry.affected_modules || []).length === 0 &&
                                  !entry.error && (
                                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500">
                                      no known modules touched
                                    </span>
                                  )}
                                {(entry.affected_modules || []).map((m) => {
                                  const isOrphan = !knownMenuKeys.has(
                                    m.menu_key,
                                  );
                                  const menu = findMenuByKey(m.menu_key);
                                  const label = menu?.label || m.menu_key;
                                  return (
                                    <span
                                      key={m.menu_key}
                                      title={
                                        isOrphan
                                          ? "In componentDictionary but not in the sidebar menu"
                                          : ""
                                      }
                                      className={[
                                        "rounded-full px-2 py-0.5 text-[10px] font-medium",
                                        isOrphan
                                          ? "bg-amber-100 text-amber-800"
                                          : "bg-brand-100 text-brand-800",
                                      ].join(" ")}
                                    >
                                      {label} · {m.files_count}
                                    </span>
                                  );
                                })}
                                {(entry.other_files || []).length > 0 && (
                                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500">
                                    other · {entry.other_files.length}
                                  </span>
                                )}
                                {entry.error && (
                                  <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-medium text-rose-700">
                                    diff failed
                                  </span>
                                )}
                              </div>
                            </div>
                            <Icons.ChevronDown
                              className={[
                                "mt-0.5 h-4 w-4 flex-none text-slate-400 transition-transform",
                                isSelected ? "rotate-180" : "",
                              ].join(" ")}
                            />
                          </button>

                          {/* Selected entry: body + module checkboxes + other files */}
                          {isSelected && (
                            <div className="border-t border-slate-200 bg-card">
                              {entry.error ? (
                                <div className="px-3 py-2 text-xs text-rose-700">
                                  {entry.error}
                                </div>
                              ) : (
                                <>
                                  {entry.body && (
                                    <pre className="max-h-40 overflow-y-auto whitespace-pre-wrap border-b border-slate-100 px-3 py-2 text-xs leading-relaxed text-slate-700">
                                      {entry.body}
                                    </pre>
                                  )}
                                  <div className="px-3 py-2">
                                    <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                                      Target module(s) — one test case will be
                                      generated for each
                                    </div>
                                    {(entry.affected_modules || []).length ===
                                    0 ? (
                                      <div className="text-xs text-slate-500">
                                        No known modules — pick a target page
                                        manually below.
                                      </div>
                                    ) : (
                                      <ul className="space-y-1">
                                        {(entry.affected_modules || []).map(
                                          (m) => {
                                            const isChecked =
                                              checkedMenuKeys.includes(
                                                m.menu_key,
                                              );
                                            const isOrphan = !knownMenuKeys.has(
                                              m.menu_key,
                                            );
                                            const menu = findMenuByKey(
                                              m.menu_key,
                                            );
                                            const label =
                                              menu?.label || m.menu_key;
                                            return (
                                              <li key={m.menu_key}>
                                                <label
                                                  title={
                                                    isOrphan
                                                      ? "This component isn't in the sidebar menu — no navigation path, so it can't be auto-generated."
                                                      : ""
                                                  }
                                                  className={[
                                                    "flex items-start gap-2 rounded-md border px-2 py-1.5 text-xs transition",
                                                    isOrphan
                                                      ? "cursor-not-allowed border-transparent bg-amber-50/40 opacity-70"
                                                      : isChecked
                                                        ? "cursor-pointer border-brand-300 bg-brand-50"
                                                        : "cursor-pointer border-transparent hover:bg-slate-50",
                                                  ].join(" ")}
                                                >
                                                  <input
                                                    type="checkbox"
                                                    checked={
                                                      isChecked && !isOrphan
                                                    }
                                                    disabled={isOrphan}
                                                    onChange={(e) => {
                                                      if (isOrphan) return;
                                                      setCheckedMenuKeys(
                                                        (prev) =>
                                                          e.target.checked
                                                            ? [
                                                                ...prev,
                                                                m.menu_key,
                                                              ]
                                                            : prev.filter(
                                                                (k) =>
                                                                  k !==
                                                                  m.menu_key,
                                                              ),
                                                      );
                                                    }}
                                                    className="mt-0.5 h-3.5 w-3.5 rounded border-slate-300 text-brand-600 focus:ring-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
                                                  />
                                                  <div className="min-w-0 flex-1">
                                                    <div className="flex items-center gap-2">
                                                      <span className="font-medium text-slate-800">
                                                        {label}
                                                      </span>
                                                      <span className="text-slate-500">
                                                        {m.files_count} file
                                                        {m.files_count === 1
                                                          ? ""
                                                          : "s"}
                                                      </span>
                                                      {isOrphan && (
                                                        <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-amber-800">
                                                          not in menu
                                                        </span>
                                                      )}
                                                    </div>
                                                    {m.sample_files?.length >
                                                      0 && (
                                                      <div className="mt-0.5 truncate font-mono text-[10px] text-slate-500">
                                                        {m.sample_files
                                                          .slice(0, 2)
                                                          .join(", ")}
                                                        {m.sample_files.length >
                                                          2 &&
                                                          ` +${m.sample_files.length - 2}`}
                                                      </div>
                                                    )}
                                                  </div>
                                                </label>
                                              </li>
                                            );
                                          },
                                        )}
                                      </ul>
                                    )}
                                  </div>

                                  {(entry.other_files || []).length > 0 && (
                                    <details className="border-t border-slate-100">
                                      <summary className="cursor-pointer px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-700">
                                        Other files ({entry.other_files.length})
                                        — no matching module
                                      </summary>
                                      <ul className="max-h-32 overflow-y-auto border-t border-slate-100 px-3 py-2 font-mono text-[10px] text-slate-600">
                                        {entry.other_files.map((f) => (
                                          <li key={f} className="truncate">
                                            {f}
                                          </li>
                                        ))}
                                      </ul>
                                    </details>
                                  )}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {sourceMode === "jira" && (
              <div className="max-w-none">
                <label className="mb-1.5 block text-sm font-medium text-slate-700">
                  Jira ticket key or URL
                </label>
                <div className="flex items-start gap-2">
                  <input
                    value={jiraTicket}
                    onChange={(e) => {
                      setJiraTicket(e.target.value);
                      // Clear stale ticket data when the user edits the key so
                      // there's no mismatch between the input and the displayed info.
                      if (jiraData) {
                        setJiraData(null);
                        setJiraError("");
                        setJiraAutoPicked(null);
                        setConnectTo("");
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        loadJiraTicket(jiraTicket, connections);
                      }
                    }}
                    placeholder="e.g. PROJ-1234 or https://your.atlassian.net/browse/PROJ-1234"
                    className={inputCls}
                  />
                  <button
                    type="button"
                    onClick={() => loadJiraTicket(jiraTicket, connections)}
                    disabled={jiraLoading || !jiraTicket.trim() || !!jiraData}
                    className="inline-flex flex-none items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {jiraLoading ? (
                      <Icons.Spinner className="h-4 w-4 animate-spin" />
                    ) : (
                      <Icons.Spark className="h-4 w-4" />
                    )}
                    Load ticket
                  </button>
                </div>

                {!jiraLoading && jiraError && (
                  <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                    <div className="font-medium">Could not load ticket</div>
                    <div className="mt-1 text-xs">{jiraError}</div>
                    <div className="mt-2 text-xs text-amber-700">
                      Check{" "}
                      <code className="rounded bg-amber-100 px-1">
                        JIRA_BASE_URL
                      </code>
                      ,{" "}
                      <code className="rounded bg-amber-100 px-1">
                        JIRA_EMAIL
                      </code>
                      , and{" "}
                      <code className="rounded bg-amber-100 px-1">
                        JIRA_API_TOKEN
                      </code>{" "}
                      in the API .env, and that the key is correct.
                    </div>
                  </div>
                )}

                {!jiraLoading && !jiraError && jiraData && (
                  <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50">
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-3 py-2 text-xs">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-sky-700">
                          Jira
                        </span>
                        <span className="font-semibold text-slate-800">
                          {jiraData.key}
                        </span>
                        {jiraData.issue_type && (
                          <span className="text-slate-500">
                            · {jiraData.issue_type}
                          </span>
                        )}
                        {jiraData.status && (
                          <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-medium text-slate-700">
                            {jiraData.status}
                          </span>
                        )}
                        {jiraData.priority && (
                          <span className="text-slate-500">
                            Priority: {jiraData.priority}
                          </span>
                        )}
                      </span>
                      {jiraData.url && (
                        <a
                          href={jiraData.url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs font-medium text-brand-700 hover:text-brand-800"
                        >
                          Open in Jira
                        </a>
                      )}
                    </div>
                    <div className="px-3 py-2">
                      <h3 className="text-sm font-semibold text-slate-900">
                        {jiraData.summary || "(no summary)"}
                      </h3>
                      {jiraData.module_page && (
                        <div className="mt-1.5 inline-flex items-center gap-1 rounded-md bg-brand-50 px-2 py-0.5 text-[11px] font-medium text-brand-800">
                          <Icons.Spark className="h-3 w-3" />
                          Module/Page: {jiraData.module_page}
                        </div>
                      )}
                      {(jiraData.components?.length ||
                        jiraData.labels?.length) > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {(jiraData.components || []).map((c) => (
                            <span
                              key={`comp-${c}`}
                              className="rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-medium text-brand-700"
                            >
                              {c}
                            </span>
                          ))}
                          {(jiraData.labels || []).map((l) => (
                            <span
                              key={`lbl-${l}`}
                              className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-medium text-slate-700"
                            >
                              {l}
                            </span>
                          ))}
                        </div>
                      )}
                      <pre className="mt-2 max-h-56 overflow-y-auto whitespace-pre-wrap rounded border border-slate-200 bg-white px-3 py-2 text-xs leading-relaxed text-slate-700">
                        {jiraData.description_text || "(no description)"}
                      </pre>

                      {/* Xray: button-triggered discovery of linked Tests / Test
                          Sets so the user sees what will be imported. */}
                      <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-700">
                            <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-violet-700">
                              Test
                            </span>
                            Linked Tests
                          </span>
                          <button
                            type="button"
                            onClick={() => loadXrayTests(jiraData.key)}
                            disabled={xrayLoading}
                            className="inline-flex items-center gap-1 rounded-md border border-violet-300 bg-white px-2.5 py-1 text-xs font-medium text-violet-700 hover:bg-violet-50 disabled:opacity-50"
                          >
                            {xrayLoading
                              ? "Checking Jira…"
                              : xrayChecked
                                ? "Re-check Jira"
                                : "Check for tests"}
                          </button>
                        </div>

                        {xrayError && (
                          <div className="mt-2 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                            {xrayError}
                          </div>
                        )}

                        {/* Mode banner — makes it explicit whether Cortex will
                            follow pre-authored Xray steps or generate from the
                            description. */}
                        {xrayChecked &&
                          !xrayLoading &&
                          !xrayError &&
                          xrayData && (
                            <div
                              className={`mt-2 rounded border px-3 py-2 text-xs ${
                                xrayData.count > 0
                                  ? "border-violet-200 bg-violet-50 text-violet-800"
                                  : "border-slate-200 bg-slate-50 text-slate-600"
                              }`}
                            >
                              {xrayData.count > 0 ? (
                                <>
                                  <span className="font-semibold">
                                    {xrayData.count} Test
                                    {xrayData.count === 1 ? "" : "s"} found
                                  </span>{" "}
                                  — steps are pre-authored in Jira; Cortex will
                                  follow them (not invent) and name each test
                                  case after the Test.
                                </>
                              ) : (
                                <>
                                  <span className="font-semibold">
                                    No Tests linked
                                  </span>{" "}
                                  — Cortex will generate test cases from the
                                  story description above.
                                </>
                              )}
                            </div>
                          )}

                        {/* Discovered tests, each expandable to show its
                            Action / Data / Expected steps. */}
                        {xrayData?.tests?.length > 0 && (
                          <label className="mt-2 flex items-start gap-2 rounded border border-violet-200 bg-violet-50/60 px-2.5 py-1.5 text-[11px] text-violet-900">
                            <input
                              type="checkbox"
                              checked={importXray}
                              onChange={(e) => setImportXray(e.target.checked)}
                              className="mt-0.5 h-3.5 w-3.5 rounded border-slate-300 text-violet-600"
                            />
                            <span>
                              <span className="font-semibold">
                                Import selected tests
                              </span>{" "}
                              — the AI writes Artifact steps against each
                              pre-written test step (one Artifact per
                              Test). Uncheck to generate from the story
                              description instead.
                            </span>
                          </label>
                        )}

                        {/* Duplicate guard — which of these are already
                            automated, so the user never builds the same
                            Playwright test twice. */}
                        {xrayCoverageLoading && (
                          <p className="mt-2 flex items-center gap-1.5 text-[11px] text-slate-500">
                            <Icons.Spinner className="h-3 w-3 animate-spin" />
                            Checking which of these already have
                            Artifacts…
                          </p>
                        )}
                        {xrayCoverage && xrayCoverage.total > 0 && (
                          <div
                            className={`mt-2 rounded border px-3 py-2 text-[11px] leading-snug ${
                              xrayCoverage.automated_keys.length > 0
                                ? "border-amber-200 bg-amber-50 text-amber-900"
                                : "border-emerald-200 bg-emerald-50 text-emerald-800"
                            }`}
                          >
                            {xrayCoverage.automated_keys.length > 0 ? (
                              <>
                                <p className="flex items-start gap-1.5 font-semibold">
                                  <Icons.Alert className="mt-0.5 h-3.5 w-3.5 flex-none" />
                                  {xrayCoverage.automated_keys.length} of{" "}
                                  {xrayCoverage.total} already have an
                                  Artifact
                                </p>
                                <p className="mt-1 pl-5">
                                  Those are un-ticked below, so pressing
                                  Generate builds only the{" "}
                                  <span className="font-semibold">
                                    {xrayCoverage.new_keys.length} new
                                  </span>{" "}
                                  one
                                  {xrayCoverage.new_keys.length === 1
                                    ? ""
                                    : "s"}
                                  . The server skips duplicates too, even if you
                                  tick one by mistake.
                                </p>
                                {xrayCoverage.drifted_keys.length > 0 && (
                                  <p className="mt-1 pl-5">
                                    {xrayCoverage.drifted_keys.join(", ")}{" "}
                                    changed in Jira since it was automated —
                                    re-author those on purpose with the override
                                    below.
                                  </p>
                                )}
                                <label className="mt-2 ml-5 flex items-start gap-2">
                                  <input
                                    type="checkbox"
                                    checked={xrayAllowDuplicates}
                                    onChange={(e) =>
                                      setXrayAllowDuplicates(e.target.checked)
                                    }
                                    className="mt-0.5 h-3.5 w-3.5 rounded border-amber-300 text-amber-600"
                                  />
                                  <span>
                                    <span className="font-semibold">
                                      Author them again anyway
                                    </span>{" "}
                                    — replaces the existing Artifact
                                    of tests that already have one.
                                  </span>
                                </label>
                              </>
                            ) : (
                              <p className="flex items-start gap-1.5">
                                <Icons.Check className="mt-0.5 h-3.5 w-3.5 flex-none" />
                                <span>
                                  None of these have an Artifact yet
                                  — everything you generate here is new.
                                </span>
                              </p>
                            )}
                          </div>
                        )}

                        {/* Background context shared with the AI. This is now
                            MANDATORY — the agent always receives the epic, story,
                            test set, test plan and preconditions; the only choice
                            left to the user is which Xray tests to import. Each
                            row shows what was discovered for this story. */}
                        {xrayData?.tests?.length > 0 &&
                          importXray &&
                          (() => {
                            const ctxItems = [
                              {
                                id: "epic",
                                label: "Epic",
                                avail: !!xrayData?.epic?.key,
                                note: xrayData?.epic?.key
                                  ? `${xrayData.epic.key}`
                                  : "none linked",
                              },
                              {
                                id: "story",
                                label: "Story",
                                avail: !!xrayData?.story_key,
                                note: xrayData?.story_key
                                  ? `${xrayData.story_key}`
                                  : "none linked",
                              },
                              {
                                id: "test_set",
                                label: "Test Set description",
                                avail:
                                  (xrayData?.test_set_keys || []).length > 0,
                                note:
                                  (xrayData?.test_set_keys || []).join(", ") ||
                                  "none linked",
                              },
                              {
                                id: "test_plan",
                                label: "Test Plan",
                                avail:
                                  (xrayData?.test_plan_keys || []).length > 0,
                                note:
                                  (xrayData?.test_plan_keys || []).join(", ") ||
                                  "none linked",
                              },
                              {
                                id: "preconditions",
                                label: "Preconditions",
                                avail: true,
                                note: "of the selected tests, if any",
                              },
                            ];
                            return (
                              <div className="mt-2 rounded border border-slate-200 bg-white px-2.5 py-2">
                                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                                  Context shared with the AI (always included)
                                </div>
                                <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                                  {ctxItems.map((o) => (
                                    <div
                                      key={o.id}
                                      className="flex items-start gap-2 text-[11px] text-slate-700"
                                      title={
                                        o.avail
                                          ? o.note
                                          : "Nothing of this type is linked to this story"
                                      }
                                    >
                                      <Icons.Check
                                        className={`mt-0.5 h-3.5 w-3.5 flex-none ${
                                          o.avail
                                            ? "text-emerald-600"
                                            : "text-slate-300"
                                        }`}
                                      />
                                      <span>
                                        <span className="font-medium">
                                          {o.label}
                                        </span>{" "}
                                        <span className="text-slate-400">
                                          ({o.note})
                                        </span>
                                      </span>
                                    </div>
                                  ))}
                                </div>
                                <p className="mt-1 text-[10px] text-slate-400">
                                  Shared as background only — the AI still
                                  follows the pre-written test steps exactly.
                                </p>
                              </div>
                            );
                          })()}
                        {xrayData?.tests?.length > 0 && (
                          <ul className="mt-3 space-y-2">
                            {xrayData.tests.map((t) => {
                              const selected = xraySelected.has(t.issue_id);
                              const expanded = xrayExpanded.has(t.issue_id);
                              const cov = xrayCoverage?.items?.[t.key] || null;
                              const done = isAlreadyAutomated(t);
                              const lockedOut = done && !xrayAllowDuplicates;
                              return (
                                <li
                                  key={t.issue_id}
                                  className={`overflow-hidden rounded-lg border bg-white ${
                                    lockedOut
                                      ? "border-slate-200 bg-slate-50/60"
                                      : selected
                                        ? "border-violet-300 ring-1 ring-violet-200"
                                        : "border-slate-200"
                                  }`}
                                >
                                  <div className="flex items-center gap-2.5 px-3 py-2.5">
                                    <input
                                      type="checkbox"
                                      checked={selected}
                                      disabled={lockedOut}
                                      title={
                                        lockedOut
                                          ? `${t.key} already has an Artifact. Tick "Author them again anyway" above to re-create it.`
                                          : undefined
                                      }
                                      onChange={() =>
                                        toggleXraySelected(t.issue_id)
                                      }
                                      className="h-4 w-4 flex-none rounded border-slate-300 text-violet-600 disabled:cursor-not-allowed disabled:opacity-40"
                                    />
                                    <button
                                      type="button"
                                      onClick={() =>
                                        toggleXrayExpanded(t.issue_id)
                                      }
                                      className="flex min-w-0 flex-1 items-center gap-2 text-left"
                                    >
                                      <span className="font-mono text-[11px] font-bold text-violet-700">
                                        {t.key}
                                      </span>
                                      <span
                                        className={`min-w-0 flex-1 truncate text-xs ${
                                          lockedOut
                                            ? "text-slate-400"
                                            : "text-slate-700"
                                        }`}
                                      >
                                        {t.summary || "(no summary)"}
                                      </span>
                                      {done && (
                                        <span
                                          className="flex-none rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 ring-1 ring-emerald-600/20"
                                          title={(cov?.testcases || [])
                                            .map((c) => c.name)
                                            .join("\n")}
                                        >
                                          Automated ({cov?.testcase_count || 0})
                                        </span>
                                      )}
                                      {cov?.drifted && (
                                        <span className="flex-none rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700 ring-1 ring-amber-600/20">
                                          steps changed
                                        </span>
                                      )}
                                      <span className="flex-none rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                                        {t.steps?.length || 0} step
                                        {(t.steps?.length || 0) === 1 ? "" : "s"}
                                      </span>
                                      <Icons.ChevronDown
                                        className={`h-3.5 w-3.5 flex-none text-slate-400 transition-transform ${
                                          expanded ? "rotate-180" : ""
                                        }`}
                                      />
                                    </button>
                                  </div>
                                  {done && (
                                    <div className="border-t border-slate-100 bg-slate-50 px-3 py-2">
                                      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                                        Existing Artifact
                                        {(cov?.testcase_count || 0) === 1
                                          ? ""
                                          : "s"}
                                      </p>
                                      <ul className="mt-1 space-y-0.5">
                                        {(cov?.testcases || []).map((c) => (
                                          <li
                                            key={c.testcasekey}
                                            className="flex flex-wrap items-center gap-1.5 text-[11px] text-slate-600"
                                          >
                                            <Icons.Check className="h-3 w-3 flex-none text-emerald-600" />
                                            <span className="font-medium text-slate-800">
                                              {c.name}
                                            </span>
                                            <span className="rounded-full bg-white px-1.5 py-0.5 text-[10px] text-slate-500 ring-1 ring-slate-200">
                                              {c.board_name}
                                            </span>
                                            <span className="text-slate-400">
                                              {c.steps_count} step
                                              {c.steps_count === 1
                                                ? ""
                                                : "s"} · {c.status}
                                            </span>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                  {expanded && (
                                    <div className="border-t border-slate-100 bg-slate-50/40 px-3 py-2.5">
                                      <table className="w-full table-fixed text-[11px]">
                                        <thead>
                                          <tr className="text-left text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                                            <th className="w-6">#</th>
                                            <th>Action</th>
                                            <th>Data</th>
                                            <th>Expected Result</th>
                                          </tr>
                                        </thead>
                                        <tbody className="align-top text-slate-700">
                                          {(t.steps || []).map((s) => (
                                            <tr
                                              key={s.step_no}
                                              className="border-t border-slate-100"
                                            >
                                              <td className="py-1 text-slate-400">
                                                {s.step_no}
                                              </td>
                                              <td className="py-1 pr-2 whitespace-pre-wrap">
                                                {s.action || "—"}
                                              </td>
                                              <td className="py-1 pr-2 whitespace-pre-wrap text-slate-500">
                                                {s.data || "—"}
                                              </td>
                                              <td className="py-1 whitespace-pre-wrap">
                                                {s.result || "—"}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        )}

                        {/* View the full context that will be sent to the AI
                            (selected Xray tests + their steps + the ticked
                            context options). */}
                        {xrayData?.tests?.length > 0 && (
                          <div className="mt-2.5">
                            <button
                              type="button"
                              onClick={previewXrayContext}
                              className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-violet-700 hover:text-violet-900 hover:underline"
                            >
                              <Icons.Eye className="h-3.5 w-3.5" />
                              View final context
                            </button>
                            {xrayCtxOpen && (
                              <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
                                <div className={`flex ${DIALOG_HEIGHT} w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-slate-200`}>
                                  <div className="flex flex-none items-center justify-between border-b border-slate-200 px-5 py-3">
                                    <h3 className="text-sm font-semibold text-slate-900">
                                      Final context sent to the AI
                                    </h3>
                                    <button
                                      type="button"
                                      onClick={() => setXrayCtxOpen(false)}
                                      className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                                    >
                                      <Icons.X className="h-5 w-5" />
                                    </button>
                                  </div>
                                  <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
                                    {xrayCtxLoading ? (
                                      <div className="flex items-center gap-2 text-sm text-slate-500">
                                        <Icons.Spinner className="h-4 w-4 animate-spin" />{" "}
                                        Building context…
                                      </div>
                                    ) : xrayCtxError ? (
                                      <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                                        {xrayCtxError}
                                      </div>
                                    ) : (
                                      <>
                                        <p className="mb-2 text-xs text-slate-500">
                                          Everything sent to the AI for this
                                          import: the selected tests with
                                          their pre-written steps, plus the
                                          background context from your ticked
                                          options. The AI authors one
                                          Artifact per test, following these
                                          steps.
                                        </p>
                                        <pre className="max-h-[55vh] overflow-y-auto whitespace-pre-wrap rounded border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] leading-relaxed text-slate-700">
                                          {xrayCtxText ||
                                            "(nothing to show — select at least one test above)"}
                                        </pre>
                                      </>
                                    )}
                                  </div>
                                  <div className="flex flex-none justify-end border-t border-slate-200 px-5 py-3">
                                    <button
                                      type="button"
                                      onClick={() => setXrayCtxOpen(false)}
                                      className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                                    >
                                      Close
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {sourceMode === "manual" && (
              <div className="max-w-none">
                <label className="mb-1.5 block text-sm font-medium text-slate-700">
                  Feature description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={5}
                  placeholder="Describe what this test case should verify…"
                  className={inputCls}
                />
              </div>
            )}
          </section>

          {/* Connection */}
          <section id="connection" className="scroll-mt-4">
            {sourceMode === "github" ? (
              /* Modules picked from the changelog entry take the place of the
                 dropdown — one test case will be generated per checked module. */
              <>
                <h2 className="text-sm font-semibold text-slate-900">
                  Target module(s) {req}
                </h2>
                <p className="mb-2 mt-0.5 text-xs text-slate-500">
                  Pulled from the changelog entry you selected above. Check one
                  or more to author a test case per module.
                </p>
                {checkedMenuKeys.length === 0 ? (
                  <div className="inline-flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                    <Icons.Spark className="h-4 w-4" />
                    Tick at least one module in the selected entry to enable
                    Generate.
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {checkedMenuKeys.map((key) => {
                      const menu = findMenuByKey(key);
                      return (
                        <span
                          key={key}
                          className="inline-flex items-center gap-1.5 rounded-full border border-brand-200 bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-800"
                        >
                          <Icons.Check className="h-3 w-3" />
                          {menu?.label || key}
                          <button
                            type="button"
                            onClick={() =>
                              setCheckedMenuKeys((prev) =>
                                prev.filter((k) => k !== key),
                              )
                            }
                            className="ml-1 rounded p-0.5 text-brand-700 hover:bg-brand-100"
                            aria-label={`Remove ${menu?.label || key}`}
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
              </>
            ) : sourceMode === "jira" &&
              jiraAutoPicked?.source === "module_page" ? (
              /* Strong match from Jira Module/Page field — no manual pick needed. */
              <>
                <h2 className="text-sm font-semibold text-slate-900">
                  Target page
                </h2>
                <p className="mb-2 mt-0.5 text-xs text-slate-500">
                  Taken from the Jira ticket&apos;s Module/Page field.
                </p>
                <div className="inline-flex items-center gap-2 rounded-lg border border-brand-200 bg-brand-50 px-3 py-2 text-sm">
                  <Icons.Spark className="h-4 w-4 text-brand-700" />
                  <span className="font-medium text-brand-800">
                    {connLabel || jiraData?.module_page}
                  </span>
                  <span className="text-xs text-brand-700">· from Jira</span>
                </div>
              </>
            ) : (
              <>
                <h2 className="text-sm font-semibold text-slate-900">
                  Connect to {req}
                </h2>
                <p className="mb-2 mt-0.5 text-xs text-slate-500">
                  {sourceMode === "jira" && jiraData?.module_page
                    ? `Jira Module/Page value "${jiraData.module_page}" didn't match any sidebar page — please pick manually.`
                    : "Link this test case to a page, keep it standalone, or pick “Login Page” to test the sign-in screen."}
                </p>
                <div className="max-w-xs">
                  <Dropdown
                    value={connectTo}
                    options={[
                      {
                        id: "__login__",
                        label: "Login Page — test the sign-in screen",
                        path: [],
                      },
                      ...connections,
                    ]}
                    onChange={setConnectTo}
                    placeholder={
                      connectionsLoading
                        ? "Loading options…"
                        : connectionsError
                          ? "Unavailable"
                          : "— select a page —"
                    }
                    disabled={connectionsLoading || Boolean(connectionsError)}
                  />
                  {connectionsError && (
                    <div className="mt-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2">
                      <p className="flex items-start gap-1.5 text-xs leading-snug text-amber-800">
                        <Icons.Alert className="mt-px h-3.5 w-3.5 flex-none text-amber-600" />
                        <span>{connectionsError}</span>
                      </p>
                      <button
                        type="button"
                        onClick={loadConnections}
                        disabled={connectionsLoading}
                        className="mt-1.5 inline-flex items-center gap-1 rounded-md border border-amber-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-amber-800 transition hover:bg-amber-50 disabled:opacity-60"
                      >
                        <Icons.Refresh
                          className={[
                            "h-3 w-3",
                            connectionsLoading ? "animate-spin" : "",
                          ].join(" ")}
                        />
                        Retry
                      </button>
                    </div>
                  )}
                  {sourceMode === "jira" &&
                    jiraAutoPicked?.source === "keyword" && (
                      <p className="mt-1 text-xs text-slate-500">
                        <Icons.Spark className="mr-1 inline-block h-3 w-3 text-brand-600" />
                        Auto-picked from ticket — {jiraAutoPicked.reason}
                      </p>
                    )}
                </div>
              </>
            )}
          </section>

          {/* AI model provider */}
          <section id="ai-model" className="scroll-mt-4">
            <h2 className="text-sm font-semibold text-slate-900">
              AI model {req}
            </h2>
            <p className="mb-3 mt-0.5 text-xs text-slate-500">
              Choose which provider and model author the test cases.
            </p>
            <div className="flex flex-wrap items-start gap-3">
              <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
                {[
                  { id: "openai", label: "OpenAI" },
                  { id: "gemini", label: "Gemini" },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setProvider(opt.id)}
                    className={[
                      "rounded-md px-3 py-1.5 text-sm font-medium transition",
                      provider === opt.id
                        ? "bg-card text-brand-700 shadow-sm ring-1 ring-slate-200"
                        : "text-slate-500 hover:text-slate-800",
                    ].join(" ")}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {/* Model dropdown — options fetched live for the chosen provider */}
              <div className="w-60">
                <Dropdown
                  value={model}
                  options={modelOptions}
                  onChange={setModel}
                  placeholder={
                    modelsLoading
                      ? "Loading models…"
                      : modelsError
                        ? "Unavailable"
                        : "— select a model —"
                  }
                  disabled={
                    modelsLoading ||
                    Boolean(modelsError) ||
                    modelOptions.length === 0
                  }
                />
                {modelsError && (
                  <p className="mt-1 text-xs text-rose-600">{modelsError}</p>
                )}
              </div>
            </div>
          </section>

          {/* AI model provider
          <section id="ai-model" className="scroll-mt-4">
            <h2 className="text-sm font-semibold text-slate-900">AI model {req}</h2>
            <p className="mb-3 mt-0.5 text-xs text-slate-500">
              Choose which model authors the test cases.
            </p>
            <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
              {[
                { id: "openai", label: "OpenAI" },
                { id: "gemini", label: "Gemini" },
              ].map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setProvider(opt.id)}
                  className={[
                    "rounded-md px-3 py-1.5 text-sm font-medium transition",
                    provider === opt.id
                      ? "bg-card text-brand-700 shadow-sm ring-1 ring-slate-200"
                      : "text-slate-500 hover:text-slate-800",
                  ].join(" ")}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </section> */}

          {/* AI Context */}
          <section id="ai-context" className="scroll-mt-4">
            <h2 className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
              <Icons.Spark className="h-4 w-4 text-brand-600" />
              Exclusive context for the AI
            </h2>
            <p className="mb-3 mt-0.5 text-xs text-slate-500">
              Extra guidance only the AI will use when generating and running
              steps (constraints, test data, environment notes, edge cases).
            </p>
            <div className="max-w-none">
              <textarea
                value={aiContext}
                onChange={(e) => setAiContext(e.target.value)}
                rows={5}
                placeholder="e.g. Use the staging environment. Test account: qa@example.com. Focus on error handling for expired tokens…"
                className={inputCls}
              />
            </div>
          </section>
        </div>

        {/* Pinned footer action */}
        <div className="flex-none border-t border-slate-100 p-4">
          {xrayAllAlreadyDone && (
            <div className="mb-3 w-full rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <span className="font-semibold">Nothing new to generate.</span>{" "}
              {(xrayData?.tests || []).length === 1
                ? "The test linked to this ticket already has"
                : `All ${(xrayData?.tests || []).length} tests linked to this ticket already have`}{" "}
              an Artifact. Tick{" "}
              <span className="font-semibold">“Author them again anyway”</span>{" "}
              above to recreate them, or pick a different ticket.
            </div>
          )}
          {generationStarted && (
            <div className="mb-3 w-full rounded-lg border border-brand-200 bg-brand-50 px-4 py-3 text-sm text-brand-800">
              <span className="font-semibold">
                Generation is running in the other tab.
              </span>{" "}
              Switch to that tab to see live progress. You can also close it —
              the result will appear in your{" "}
              <span className="font-semibold">Inbox</span>.
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-4">
            {/* Readiness summary — a disabled Generate always says why. */}
            <div className="min-w-0 flex-1">
              {canGenerate ? (
                <p className="flex items-center gap-1.5 text-sm font-medium text-emerald-700">
                  <Icons.Check className="h-4 w-4 flex-none" />
                  Ready to generate
                  {sourceMode === "jira" && importXray && selectedXrayCount > 0
                    ? ` — ${selectedXrayCount} test${selectedXrayCount === 1 ? "" : "s"} selected`
                    : ""}
                  .
                </p>
              ) : (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                  <span className="font-semibold uppercase tracking-wider text-slate-400">
                    To generate
                  </span>
                  {readiness.map((r) => (
                    <span
                      key={r.label}
                      className={`inline-flex items-center gap-1 ${
                        r.ok ? "text-emerald-600" : "text-slate-500"
                      }`}
                    >
                      {r.ok ? (
                        <Icons.Check className="h-3.5 w-3.5 flex-none" />
                      ) : (
                        <span className="h-1.5 w-1.5 flex-none rounded-full bg-slate-300" />
                      )}
                      {r.label}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="flex flex-none items-center gap-3">
              <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={autoLogin}
                  onChange={(e) => setAutoLogin(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-600"
                />
                Auto login
              </label>
              <button
                type="button"
                onClick={startGenerate}
                disabled={!canGenerate || generationStarted}
                title={generateDisabledReason || "Generate Artifacts"}
                className="inline-flex items-center gap-2 rounded-lg bg-brand-700 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {generationStarted ? (
                  <>
                    <Icons.Spinner className="h-4 w-4 animate-spin" />
                    Generating…
                  </>
                ) : (
                  <>
                    <Icons.Spark className="h-4 w-4" />
                    Generate test cases
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
